import { useState } from "react";
import { Breadcrumbs, DateTimeDisplay, StatusMessage } from "../../components/ui";
import Modal from "../../components/ui/Modal";
import { ModalContent } from "../../components/ui/ModalContent";
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";
import { ScheduleEditor } from "../../features/schedules/ScheduleEditor";
import { ScheduleList } from "../../features/schedules/ScheduleList";
import { useSchedules } from "../../features/schedules/useSchedules";
import type { CreateScheduleRequest, ScheduleDetail, ScheduleSummary } from "../../services/configuration/configuration.types";

const Schedules = () => {
    const data = useSchedules();
    const breadcrumbs = useBreadcrumbs({ currentPageTitle: "Schedules" });
    const [editor, setEditor] = useState<{ schedule: ScheduleDetail | null } | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<ScheduleSummary | null>(null);
    const busy = data.submitting || data.loadingDetails;
    const openCreate = () => {
        if (busy) {return;}
        data.setError(null);
        data.setModalError(null);
        setEditor({ schedule: null });
    };
    const openEdit = async (schedule: ScheduleSummary) => {
        if (busy) {return;}
        const detail = await data.loadDetails(schedule.id);
        if (detail) {
            setEditor({ schedule: detail });
            globalThis.scrollTo({ top: 0, behavior: "smooth" });
        }
    };
    const save = async (values: CreateScheduleRequest) => {
        if (await data.save(values, editor?.schedule ?? null)) 
            {setEditor(null);}
    };
    const remove = async () => {
        if (deleteTarget && await data.remove(deleteTarget.id)) {
            setDeleteTarget(null);
        }
    };

    return (
        <main>
            <div className="container mb-2">
                {editor && <ScheduleEditor schedule={editor.schedule} submitting={data.submitting} error={data.modalError}
                    onClearError={() => data.setModalError(null)} onSave={save} onCancel={() => setEditor(null)} />}
                <Modal isOpen={deleteTarget !== null} title="Delete Schedule" headerClass="modal__header--danger"
                    okButtonLoading={data.submitting} cancelButtonLoading={data.submitting}
                    content={<>
                        <ModalContent heading="Delete Schedule" message="Are you sure you want to delete this schedule?" />
                        {data.modalError && <StatusMessage kind="error" onClose={() => data.setModalError(null)}>{data.modalError}</StatusMessage>}
                    </>}
                    onConfirm={remove} onCancel={() => { if (!data.submitting) setDeleteTarget(null); }} />
                <section className="page">
                    <div className="heading__section">
                        <div className="heading__title-group">
                            <h2 className="heading__title heading__title--schedules">Schedules</h2>
                            <DateTimeDisplay />
                            <Breadcrumbs items={breadcrumbs} />
                        </div>
                        <div>{!editor && <button type="button" className="btn btn-add-new" disabled={data.loading || busy || !!deleteTarget}
                            onClick={openCreate}>Add Schedule</button>}</div>
                    </div>
                </section>
                {data.loadingDetails && <p role="status">Loading schedule details…</p>}
                <section className="page">
                    {data.success && <StatusMessage kind="success" onClose={() => data.setSuccess(null)}>{data.success.message}</StatusMessage>}
                    {data.error && <StatusMessage kind="error" onClose={() => data.setError(null)}>{data.error}</StatusMessage>}
                    <div className="alarm-content">
                        <ScheduleList schedules={data.schedules} loading={data.loading} busy={busy || !!editor || !!deleteTarget}
                            editorOpen={!!editor} onEdit={schedule => { void openEdit(schedule); }}
                            onDelete={schedule => { data.setModalError(null); setDeleteTarget(schedule); }} />
                    </div>
                </section>
            </div>
        </main>
    );
};

export default Schedules;
